/* eslint-disable react/jsx-pascal-case */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
   Table, Space, Tooltip, Button, Popconfirm,
   Modal, Tabs, Skeleton, Empty
} from 'antd';
import ResponsiveBreadcrumb from '../../../components/breadcrumb/ResponsiveBreadcrumb';
import {
   ProfileOutlined, DeleteOutlined,
   ClockCircleOutlined, CheckCircleOutlined, RollbackOutlined
} from '@ant-design/icons';
import axios from 'axios';
import Utils from '../../../misc/Utils';
import Enums from '../../../misc/Enums';
import StudentPerformanceTaskDetailsSubmit from './StudentPerformanceTaskDetailsSubmit';
import { isPlagiarismCheckerEnabled } from '../../HomeworksPage/plagiarismUtils';
import ProcessingModal from '../../../components/processing/ProcessingModal';
import {
   getMicrocredentialsTaskModule,
   detectMicrocredentialsTaskMode,
   MICROCREDENTIALS_TASK_MODES,
} from '../../../config/microcredentialsTaskModules';
import {
   listStudentTasks,
   submitStudentTask,
   unsubmitStudentTask,
} from '../../../services/MicrocredentialsStudentTaskService';
import '../../common/BraineeModule.css';

function getTaskDate(record) {
   return record?.performance_task_date ?? record?.homework_date ?? record?.task_date ?? record?.date_assigned ?? '';
}

function getSubmitDate(record) {
   return record?.submission_date ?? record?.submit_date ?? '';
}

function toArray(payload) {
   if (Array.isArray(payload)) {
      return payload;
   }

   if (Array.isArray(payload?.data)) {
      return payload.data;
   }

   if (Array.isArray(payload?.items)) {
      return payload.items;
   }

   return [];
}

const STUDENT_UPLOADS_PATH = process.env.REACT_APP_S3_PERFORMANCE_TASK_STUDENTS_PATH || '';

function normalizeSubmissionStoredPath(storedPath) {
   const raw = String(storedPath || '').trim();

   if (!raw) {
      return '';
   }

   if (/^(https?:)?\/\//i.test(raw) || /^(data|blob):/i.test(raw)) {
      return raw;
   }

   if (raw.includes('/') || raw.includes('\\')) {
      return raw;
   }

   if (!STUDENT_UPLOADS_PATH) {
      return raw;
   }

   return `${STUDENT_UPLOADS_PATH}/${raw}`;
}

function getFileExtensionFromName(fileName) {
   const raw = String(fileName || '').trim();

   if (!raw || !raw.includes('.')) {
      return '';
   }

   return `.${raw.split('.').pop().toLowerCase()}`;
}

function normalizeUploadedDocumentsResponse(uploadResponse) {
   if (Array.isArray(uploadResponse)) {
      return uploadResponse;
   }

   if (typeof uploadResponse === 'string') {
      const trimmed = uploadResponse.trim();

      if (!trimmed) {
         return [];
      }

      try {
         const parsed = JSON.parse(trimmed);
         return Array.isArray(parsed) ? parsed : [parsed];
      } catch (error) {
         return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
      }
   }

   return uploadResponse ? [uploadResponse] : [];
}

function buildSubmissionDocumentPayload(uploadResponse, fileList) {
   const uploadedItems = normalizeUploadedDocumentsResponse(uploadResponse);

   return JSON.stringify(
      uploadedItems
         .map((item, index) => {
            if (item && typeof item === 'object' && item.s3_path) {
               const originalFileName = item.original_filename || fileList[index]?.name || item.s3_path.split(/[\\/]/).pop() || `Attachment ${index + 1}`;

               return {
                  original_filename: originalFileName,
                  extension_name: item.extension_name || getFileExtensionFromName(originalFileName),
                  s3_path: normalizeSubmissionStoredPath(item.s3_path),
               };
            }

            const storedPath = String(item || '').trim();
            if (!storedPath) {
               return null;
            }

            const originalFileName = fileList[index]?.name || storedPath.split(/[\\/]/).pop() || `Attachment ${index + 1}`;

            return {
               original_filename: originalFileName,
               extension_name: getFileExtensionFromName(originalFileName),
               s3_path: normalizeSubmissionStoredPath(storedPath),
            };
         })
         .filter(Boolean)
   );
}

function normalizePerformanceTask(record, taskLabel) {
   return {
      ...record,
      id: record?.id ?? record?.performance_task_id ?? record?.task_id,
      title: record?.title ?? record?.Title ?? record?.performance_task_title ?? taskLabel,
      teacher: record?.teacher ?? record?.Teacher ?? record?.assigned_by_name ?? '-',
      subject: record?.subject ?? record?.Subject ?? record?.subject_name ?? '-',
      topic: record?.topic ?? record?.Topic ?? record?.topic_name ?? record?.topic_title ?? '-',
      remarks: record?.remarks ?? record?.Remarks ?? '',
      submission_date: getSubmitDate(record),
      evaluation_date: record?.evaluation_date ?? record?.Evaluation_date ?? '',
      score: record?.score ?? 0,
      max_score: record?.max_score ?? record?.maxScore ?? null,
      submit_id: record?.submit_id ?? record?.submitted_id ?? null,
      evaluation_id: record?.evaluation_id ?? null,
      returned: Boolean(record?.returned),
      plagiarism_checker: isPlagiarismCheckerEnabled(record?.plagiarism_checker ?? record?.plagiarismChecker ?? record?.Plagiarism_checker),
   };
}

function getTaskStatus(record) {
   if (record.returned) {
      return { label: 'Returned', className: ' is-warning' };
   }

   if (record.evaluation_id !== null) {
      return { label: 'Completed', className: ' is-success' };
   }

   if (record.submit_id !== null) {
      return { label: 'For Evaluation', className: ' is-muted' };
   }

   return { label: 'Not Submitted', className: ' is-danger' };
}

export function StudentPerformanceTasks({ mode = detectMicrocredentialsTaskMode(window.location?.pathname) } = {}) {
   const search = window.location.search;
   const query = new URLSearchParams(search);
   const subject = query.get('s');
   const subjectname = query.get('sn');
   const moduleConfig = getMicrocredentialsTaskModule(mode);
   const taskLabel = moduleConfig.label;
   const taskLabelLower = taskLabel.toLowerCase();

   const [performanceTaskID, setPerformanceTaskID] = useState('');
   const [performanceTaskList, setPerformanceTaskList] = useState([]);
   const [performanceTaskListLoading, setPerformanceTaskListLoading] = useState(false);
   const [submitting, setSubmitting] = useState(false);
   const [detailsModalVisible, setDetailsModalVisible] = useState(false);
   const [canSubmit, setCanSubmit] = useState(true);
   const [returned, setReturned] = useState(false);
   const [remarks, setRemarks] = useState(null);
   const [plagiarismCheckerEnabled, setPlagiarismCheckerEnabled] = useState(false);
   const [activeTab, setActiveTab] = useState('1');
   const [isCompactListView, setIsCompactListView] = useState(window.innerWidth <= 768);
   const [statusCounts, setStatusCounts] = useState({
      notSubmitted: 0,
      submitted: 0,
      returned: 0,
   });

   const submitFormRef = useRef(null);
   const userId = Utils.getCurrentUserInfo(Enums.UserInfo.id);
   const userRole = Utils.getCurrentUserInfo(Enums.UserInfo.role);

   useEffect(() => {
      Utils.verifyJwt();
      refreshList('1');
   }, [mode, subject]);

   useEffect(() => {
      const updateCompactListView = () => {
         setIsCompactListView(window.innerWidth <= 768);
      };

      updateCompactListView();
      window.addEventListener('resize', updateCompactListView);

      return () => {
         window.removeEventListener('resize', updateCompactListView);
      };
   }, []);

   const loadStatusCounts = () => {
      Promise.all([
         listStudentTasks({ id: userId, subject, submitted: false }, mode),
         listStudentTasks({ id: userId, subject, submitted: true }, mode),
      ]).then(([notSubmittedResponse, submittedResponse]) => {
         const notSubmittedItems = toArray(notSubmittedResponse)
            .map((item) => normalizePerformanceTask(item, taskLabel))
            .filter((item) => item.returned !== true);
         const submittedItems = toArray(submittedResponse)
            .map((item) => normalizePerformanceTask(item, taskLabel));

         setStatusCounts({
            notSubmitted: notSubmittedItems.length,
            submitted: submittedItems.filter((item) => item.returned !== true).length,
            returned: submittedItems.filter((item) => item.returned === true).length,
         });
      }).catch(() => {
         setStatusCounts({ notSubmitted: 0, submitted: 0, returned: 0 });
      });
   };

   const performanceTaskColumns = [
      {
         title: 'Title',
         key: 'title',
         dataIndex: 'title',
         width: '22%',
      },
      {
         title: 'Teacher',
         key: 'teacher',
         dataIndex: 'teacher',
         width: '12%',
      },
      {
         title: 'Course / Topic',
         key: 'subject',
         render: (record) => [record.subject, record.topic].filter(Boolean).join(' / '),
         width: '16%',
      },
      {
         title: 'Task Date',
         key: 'task_date',
         render: (_, record) => getTaskDate(record),
         width: '12%',
      },
      {
         title: 'Submission Date',
         key: 'submission_date',
         render: (_, record) => getSubmitDate(record),
         width: '12%',
      },
      {
         title: 'Status',
         key: 'status',
         render: (record) => getTaskStatus(record).label,
         width: '10%',
      },
      {
         title: 'Action',
         key: 'action',
         hidden: userRole === 'Parent',
         render: (payload) => (
            userRole !== 'Parent'
               ? <Space size="small">
                  <Tooltip placement="left" title={`Show ${taskLabel} Details`}>
                     <ProfileOutlined
                        color="#4abec7"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleShowPerformanceTaskDetails(payload.id, (payload.submit_id === null || payload.returned), payload.returned, payload.remarks, payload.plagiarism_checker)}
                     />
                  </Tooltip>
                  {payload.submit_id !== null && payload.evaluation_id === null && (
                     <Tooltip placement="left" title={`Unsubmit ${taskLabel}`}>
                        <Popconfirm
                           title={`Are you sure you want to unsubmit your ${taskLabelLower}?`}
                           onConfirm={() => handleRemoveSubmission(payload.submit_id)}
                           okText="Yes"
                           cancelText="No"
                        >
                           <DeleteOutlined color="#4abec7" style={{ cursor: 'pointer' }} />
                        </Popconfirm>
                     </Tooltip>
                  )}
               </Space>
               : <></>
         ),
         width: '8%',
      },
   ].filter((item) => !item.hidden);

   const handleSearchPerformanceTasks = (submitted, returnedOnly) => {
      setPerformanceTaskListLoading(true);

      listStudentTasks({ id: userId, subject, submitted }, mode).then((response) => {
         const data = toArray(response).map((item) => normalizePerformanceTask(item, taskLabel));
         const filtered = returnedOnly === true
            ? data.filter((item) => item.returned === true)
            : data.filter((item) => item.returned !== true);
         setPerformanceTaskList(filtered);
      }).catch(() => {
         setPerformanceTaskList([]);
      }).finally(() => {
         setPerformanceTaskListLoading(false);
      });
   };

   const handleRemoveSubmission = (id) => {
      setPerformanceTaskListLoading(true);
      unsubmitStudentTask(id, mode).then((response) => {
         if (response) {
            Modal.success({
               title: taskLabel,
               content: `${taskLabel} unsubmitted.`,
               centered: true,
            });
            refreshList(activeTab);
         }
      }).catch((error) => {
         Modal.error({
            title: taskLabel,
            content: error?.response?.data || 'Unable to unsubmit record.',
            centered: true,
         });
      }).finally(() => {
         setPerformanceTaskListLoading(false);
      });
   };

   const handleShowPerformanceTaskDetails = (id, allowSubmit, isReturned, nextRemarks, nextPlagiarismCheckerEnabled) => {
      setRemarks(nextRemarks);
      setReturned(isReturned);
      setCanSubmit(allowSubmit);
      setPerformanceTaskID(id);
      setPlagiarismCheckerEnabled(isPlagiarismCheckerEnabled(nextPlagiarismCheckerEnabled));
      setDetailsModalVisible(true);
   };

   const handleSaveSubmittedPerformanceTask = (data, fileForUpload) => {
      setSubmitting(true);

      const completeSubmission = (payload) => {
         submitStudentTask(payload, mode).then((response) => {
            if (response) {
               Modal.success({
                  title: taskLabel,
                  content: `${taskLabel} submitted.`,
                  centered: true,
                  onOk: () => { setDetailsModalVisible(false); },
               });
               refreshList(activeTab);
            }
         }).catch((error) => {
            Modal.error({
               title: taskLabel,
               content: error?.response?.data || 'Unable to submit record.',
               centered: true,
               onOk: () => { setDetailsModalVisible(false); },
            });
         }).finally(() => {
            setSubmitting(false);
         });
      };

      if (fileForUpload.length > 0) {
         const formData = new FormData();
         fileForUpload.forEach((file) => {
            formData.append('files', file);
         });

         const uploadEndpoint = plagiarismCheckerEnabled ? 'uploadWithPlagiarism' : 'uploadMultiple';
         const uploadUrl = `${process.env.REACT_APP_API_S3_UPLOAD}/${uploadEndpoint}?s3path=${process.env.REACT_APP_S3_PERFORMANCE_TASK_STUDENTS_PATH}`;
         axios.post(uploadUrl, formData).then((response) => {
            data.document = buildSubmissionDocumentPayload(response.data, fileForUpload);
            completeSubmission(data);
         }).catch((error) => {
            setSubmitting(false);
            Modal.error({
               title: taskLabel,
               content: error?.response?.data || 'Unable to upload file.',
               centered: true,
            });
         });
      } else {
         completeSubmission(data);
      }
   };

   const refreshList = (tab) => {
      loadStatusCounts();

      switch (tab) {
         case '1':
            handleSearchPerformanceTasks(false, false);
            break;
         case '2':
            handleSearchPerformanceTasks(true, false);
            break;
         case '3':
            handleSearchPerformanceTasks(true, true);
            break;
         default:
            break;
      }
   };

   const renderTableSkeleton = () => (
      <div className="brainee-module-skeleton-table">
         <div className="brainee-module-skeleton-header">
            <Skeleton.Input active size="small" className="brainee-module-skeleton-title" />
            <Skeleton.Input active size="small" className="brainee-module-skeleton-chip" />
            <Skeleton.Input active size="small" className="brainee-module-skeleton-chip" />
            <Skeleton.Button active size="small" className="brainee-module-skeleton-action" />
         </div>
         {Array.from({ length: 7 }).map((_, index) => (
            <div className="brainee-module-skeleton-row" key={`student-performance-task-table-skeleton-${index}`}>
               <Skeleton.Input active size="small" className="brainee-module-skeleton-cell is-wide" />
               <Skeleton.Input active size="small" className="brainee-module-skeleton-cell" />
               <Skeleton.Input active size="small" className="brainee-module-skeleton-cell" />
               <Skeleton.Button active size="small" className="brainee-module-skeleton-action" />
            </div>
         ))}
      </div>
   );

   const getCardValue = (value) => {
      if (value === null || value === undefined || value === '') {
         return '—';
      }

      return value;
   };

   const renderCardField = (label, value, { fullWidth = false } = {}) => (
      <div className={`brainee-module-record-field${fullWidth ? ' is-full' : ''}`}>
         <span className="brainee-module-record-label">{label}</span>
         <span className="brainee-module-record-value">{getCardValue(value)}</span>
      </div>
   );

   const renderCardList = () => (
      <div className="brainee-module-record-list-region">
         <div className="brainee-module-record-list">
            {performanceTaskList.map((record, index) => {
               const status = getTaskStatus(record);
               const cardKey = record.id ?? record.submit_id ?? `${record.title}-${index}`;

               return (
                  <article className="brainee-module-record-card" key={cardKey}>
                     <div className="brainee-module-record-header">
                        <div className="brainee-module-record-heading">
                           <div className="brainee-module-record-header-row">
                              <span className="brainee-module-record-kicker">{taskLabel}</span>
                              <span className={`brainee-module-record-pill${status.className}`}>{status.label}</span>
                           </div>
                           <h3>{getCardValue(record.title)}</h3>
                           <p>{getCardValue(getTaskDate(record))}</p>
                        </div>
                     </div>
                     <div className="brainee-module-record-meta">
                        {renderCardField('Teacher', record.teacher)}
                        {renderCardField('Course / Topic', [record.subject, record.topic].filter(Boolean).join(' / '), { fullWidth: true })}
                        {renderCardField('Submission Date', getSubmitDate(record))}
                        {renderCardField('Evaluation Date', record.evaluation_date)}
                        {renderCardField('Remarks', record.remarks, { fullWidth: true })}
                     </div>
                     <div className="brainee-module-record-actions">
                        <Space size="small">
                           <Tooltip placement="left" title={`Show ${taskLabel} Details`}>
                              <ProfileOutlined
                                 color="#4abec7"
                                 style={{ cursor: 'pointer' }}
                                 onClick={() => handleShowPerformanceTaskDetails(record.id, (record.submit_id === null || record.returned), record.returned, record.remarks, record.plagiarism_checker)}
                              />
                           </Tooltip>
                           {record.submit_id !== null && record.evaluation_id === null && (
                              <Tooltip placement="left" title={`Unsubmit ${taskLabel}`}>
                                 <Popconfirm
                                    title={`Are you sure you want to unsubmit your ${taskLabelLower}?`}
                                    onConfirm={() => handleRemoveSubmission(record.submit_id)}
                                    okText="Yes"
                                    cancelText="No"
                                 >
                                    <DeleteOutlined color="#4abec7" style={{ cursor: 'pointer' }} />
                                 </Popconfirm>
                              </Tooltip>
                           )}
                        </Space>
                     </div>
                  </article>
               );
            })}
         </div>
         <div className="brainee-module-record-list-footer">Total Records: {performanceTaskList.length}</div>
      </div>
   );

   const renderPerformanceTaskTable = () => (
      performanceTaskListLoading
         ? renderTableSkeleton()
         : performanceTaskList.length > 0
            ? (
               isCompactListView
                  ? renderCardList()
                  : <div className="brainee-module-table-shell">
                     <Table
                        className="brainee-module-table"
                        rowKey={(record) => record.id ?? record.submit_id ?? record.title}
                        size="small"
                        columns={performanceTaskColumns}
                        dataSource={performanceTaskList}
                        pagination={false}
                        scroll={{
                           x: '1100px',
                           y: 'calc(100svh - 20.5rem)',
                        }}
                        footer={() => <div className="brainee-module-table-footer">Total Records: {performanceTaskList.length}</div>}
                     />
                  </div>
            )
            : <div className="brainee-module-state">
               <Empty description="No records found" />
            </div>
   );

   return (
      <>
         <ProcessingModal processing={submitting} title="Brainee Microcredentials" />
         <div className="dashboard-area-all brainee-module-page">
            <div className="dashboard-item brainee-module-shell">
               <div className="brainee-module-breadcrumb">
                  <ResponsiveBreadcrumb
                     items={[
                        { key: 'home', node: <Link to="/home">Home</Link> },
                        { key: 'mc', node: 'Brainee Microcredentials' },
                        { key: 'pt', node: <Link to={moduleConfig.studentSubjectsRoute}>{taskLabel}</Link> },
                        { key: 'subject', node: subjectname },
                     ]}
                  />
               </div>

               <section className="brainee-module-hero">
                  <div>
                     <span className="brainee-module-kicker">{taskLabel}</span>
                     <h1>{subjectname || moduleConfig.myLabelPlural}</h1>
                     <p>{`Track ${taskLabelLower}s by status and open submissions or returned work when you need them.`}</p>
                     <div className="brainee-module-meta">
                        <span><ClockCircleOutlined /> {performanceTaskListLoading ? 'Loading' : statusCounts.notSubmitted} not submitted</span>
                        <span><CheckCircleOutlined /> {performanceTaskListLoading ? 'Loading' : statusCounts.submitted} submitted</span>
                        <span><RollbackOutlined /> {performanceTaskListLoading ? 'Loading' : statusCounts.returned} returned</span>
                     </div>
                  </div>
               </section>

               <section className="brainee-module-panel">
                  <Tabs className="brainee-module-tabs" activeKey={activeTab} centered onChange={(key) => { setActiveTab(key); refreshList(key); }}>
                     <Tabs.TabPane tab={<Tooltip placement="top" title="Not Submitted"><span className="brainee-module-tab-label"><ClockCircleOutlined /> Not Submitted</span></Tooltip>} key="1">
                        {renderPerformanceTaskTable()}
                     </Tabs.TabPane>
                     <Tabs.TabPane tab={<Tooltip placement="top" title="Submitted"><span className="brainee-module-tab-label"><CheckCircleOutlined /> Submitted</span></Tooltip>} key="2">
                        {renderPerformanceTaskTable()}
                     </Tabs.TabPane>
                     <Tabs.TabPane tab={<Tooltip placement="top" title="Returned"><span className="brainee-module-tab-label"><RollbackOutlined /> Returned</span></Tooltip>} key="3">
                        {renderPerformanceTaskTable()}
                     </Tabs.TabPane>
                  </Tabs>
               </section>
            </div>
         </div>

         <Modal
            title={returned ? `${taskLabel} (Returned)` : taskLabel}
            centered
            destroyOnClose={true}
            onCancel={() => { setDetailsModalVisible(false); }}
            open={detailsModalVisible}
            className="performance-task-details-modal"
            width="60%"
            footer={[
               <Button key="cancel" shape="round" onClick={() => setDetailsModalVisible(false)}>
                  {canSubmit ? 'Cancel' : 'Close'}
               </Button>,
               canSubmit && (
                  <Button
                     key="submit"
                     type="primary"
                     shape="round"
                     disabled={!canSubmit}
                     loading={submitting}
                     onClick={() => submitFormRef.current?.submitHomework()}
                  >
                     Submit
                  </Button>
               ),
            ]}
         >
            <div className="performance-task-details-modal-content">
               <StudentPerformanceTaskDetailsSubmit
                  performanceTaskID={performanceTaskID}
                  onSubmitCallback={handleSaveSubmittedPerformanceTask}
                  canSubmit={canSubmit}
                  returned={returned}
                  remarks={remarks}
                  submitRef={submitFormRef}
                  mode={mode}
               />
            </div>
         </Modal>
      </>
   );
}
